import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router";
import "./App.css";
import ScrollToTop from "./utils/ScrollToTop";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CourseWrapper from "./CourseWrapper";
import { wsURL } from "./api/api";
import { useAuth } from "./features/auth/AuthContext";
import useWebSocket from "./hooks/useWebSocket";
import Dashboardheader from "./pages/home/components/dashboard/Dashboardheader";
import CheckRoute from "./routes/checkRoutes";
import PrivateRoute from "./routes/privateRoutes";
import ErrorBoundaryWithNavigation from "./ErrorBoundaryWithNavigation"; // Make sure this is imported correctly
import LoadingComponent from "./components/LoadingComponent/LoadingComponent";
import ChatDrawerWrapper from "./components/chatBotWrapper/chatBotWrapper";
import api from "./api/api";
import {
  adminroutes,
  commonroutes,
  coursesroutes,
  landingpages,
  studentroutes,
  superadminroutes,
  teacherroutes,
} from "./routes/routes";
import { updateGlobalState } from "./state/global/slice";
import SeoHead from "./components/SeoHead/SeoHead";
import { SidebarDrawerProvider } from "./context/SidebarDrawerContext";
import { getBranding } from "./config/brandingConfig";
import { resumeAiCourseStatusPolling } from "./pages/home/components/dashboard/student/teacher/aiCourseStatusPolling";

const queryClient = new QueryClient();


function App() {
  const globalState = useSelector((state) => state.globalState);
  const dispatch = useDispatch();
  const { token } = useAuth();
  const email = useSelector((state) => state.globalState.email);

  // Dynamic favicon and title based on brand
  useEffect(() => {
    const branding = getBranding(window.location.hostname);
    document.title = branding.appName || "ProSchool";
    const link =
      document.querySelector("link[rel~='icon']") ||
      document.createElement("link");
    link.rel = "icon";
    link.href = branding.favicon || "/favicon.ico";
    document.head.appendChild(link);
    const title = process.env.REACT_APP_TITLE;

    if (title) {
      document.title = title;
    } else {
      document.title = "";
    }
  }, []);

  // Resume AI course generation polling after a page refresh, on whatever route
  // the user lands on. No-op when there is no persisted session or it's already running.
  useEffect(() => {
    if (token) resumeAiCourseStatusPolling(token);
  }, [token]);

  const id =
    globalState?.role === "Student"
      ? globalState?.student_details?.data?.student_id
      : globalState?.role === "Teacher"
        ? globalState?.teacher_details?.data?.teacher_id
        : globalState?.school_details?.school_id;

  const shouldConnectWebSocket = Boolean(token && id);
  const { notifications } = useWebSocket(
    shouldConnectWebSocket
      ? `${wsURL}ws/notification/${id}/?token=${token}`
      : null,
  );

  // //fetching current loaction of the user
  // useEffect(() => {
  //   const fetchUserCountry = async () => {
  //     if (navigator.geolocation) {
  //       navigator.geolocation.getCurrentPosition(
  //         async (position) => {
  //           const { latitude, longitude } = position.coords;
  //           try {
  //             const response = await api.get(
  //               `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`,
  //             );

  //             const addressComponents =
  //               response.data?.results?.[0]?.address_components ?? [];

  //             const countryComponent = addressComponents.find((c) =>
  //               c.types?.includes("country"),
  //             );

  //             const isIndia =
  //               countryComponent?.short_name?.toUpperCase() === "IN" ||
  //               countryComponent?.long_name?.toLowerCase() === "india";

  //             dispatch(updateGlobalState({ key: "isIndia", value: isIndia }));
  //           } catch (error) {
  //             console.error("Failed to fetch country code:", error);
  //           }
  //         },
  //         (error) => {
  //           console.error("Geolocation error:", error);
  //         },
  //       );
  //     }
  //   };

  //   fetchUserCountry();
  // }, []); //  runs only once
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Suspense
          fallback={
            <LoadingComponent loading_text="Educating the Future, Loading Now." />
          }
        >
          <ErrorBoundaryWithNavigation token={token} userEmail={email}>
            <SidebarDrawerProvider> {/* Links the header hamburger to the dashboard's mobile sidebar drawer */}
            <div className="App">
              {token && (
                <>
                  <Dashboardheader
                    notifications={notifications}
                    hasNewNotification={notifications?.length > 0}
                  />
                  <ChatDrawerWrapper />
                </>
              )}
              <ScrollToTop />
              <SeoHead />
              <Routes>
                {[
                  ...landingpages,
                  ...commonroutes,
                  ...studentroutes,
                  ...teacherroutes,
                  ...coursesroutes,
                  ...adminroutes,
                  ...superadminroutes,
                ].map((route, index) => {
                  const {
                    path,
                    component: Component,
                    isPrivate,
                    isCheckRoute,
                    hasWrapper,
                  } = route;

                  if (isCheckRoute) {
                    return (
                      <Route
                        key={index}
                        path={path}
                        element={<CheckRoute component={Component} />}
                      />
                    );
                  }

                  if (isPrivate) {
                    return (
                      <Route
                        key={index}
                        path={path}
                        element={<PrivateRoute element={<Component />} />}
                      />
                    );
                  }

                  if (hasWrapper) {
                    return (
                      <Route
                        key={index}
                        path={path}
                        element={
                          <CourseWrapper>
                            <PrivateRoute element={<Component />} />
                          </CourseWrapper>
                        }
                      />
                    );
                  }

                  return (
                    <Route key={index} path={path} element={<Component />} />
                  );
                })}
              </Routes>
            </div>
            </SidebarDrawerProvider>
          </ErrorBoundaryWithNavigation>
        </Suspense>
        <ToastContainer enableMultiContainer />
        <ToastContainer
          enableMultiContainer
          containerId="archive-toast"
          position="top-right"
          className="!w-[98vw] sm:!w-[40vw]"
        />
      </Router>
    </QueryClientProvider>
  );
}

export default App;